import { useCallback } from 'preact/hooks';

import { getTracerModules } from '@apptracer/sdk/lite';

import { newMessageTemplate } from '../components/WidgetPanel/WidgetPanel.constants';
import { usePhoneContext } from '../context/PhoneContext';
import { useVkCaptchaContext } from '../context/VkCaptchaContext';
import { useWidget } from '../context/WidgetContext';
import type { Message, SubmitMessageProps } from '../types/messages';
import { WebsocketEvents, WebsocketMessageContentType } from '../types/ws-events';
import { storageManager, tokenManager } from '../utils';

import { packageName } from '../../config';
import { HAS_WIDGET_HISTORY } from '../constants/localStorage';
import { useStompSender } from './useStompSender';
import { useWidgetCsat } from './useWidgetCsat';

/** Хук для работы с редактором в виджете */
export const useWidgetEditorArea = () => {
  const tracerError = getTracerModules(packageName).error;

  const {
    setMessages,
    setEditorLock,
    editorLock,
    entryPointGateway,
    requirePhone,
    setHasMaxUserPhone,
    localeMessages,
  } = useWidget();
  const { handleCaptchaOpen } = useVkCaptchaContext();
  const { phoneNumber, selectedCountry } = usePhoneContext();

  /** Обработка успешной отправки сообщения через STOMP */
  const handleSuccessPublish = () => {
    try {
      storageManager?.setItem(HAS_WIDGET_HISTORY, '1');
    } catch (_) {
      tracerError?.error('Не удалось установить флаг в хранилище', {
        keys: {
          flag: HAS_WIDGET_HISTORY,
        },
      });
    }
  };

  /** Хук для отправки сообщений через STOMP */
  const { send } = useStompSender<Message>({
    onAfterPublish: handleSuccessPublish,
  });

  /** Показ редактора */
  const unlockEditor = () => setEditorLock?.(null);

  /** Работа с CSAT в виджете */
  const { disableLatestCsatButtons } = useWidgetCsat();

  /** Отправка сообщения из виджета */
  const onSubmit = useCallback(
    (data: SubmitMessageProps) => {
      if (!tokenManager.getToken() || (tokenManager.getToken() && tokenManager.getIsTokenExpired())) {
        handleCaptchaOpen();
        return;
      }

      let cleanPhoneNumber: string | undefined = undefined;
      if (requirePhone && phoneNumber && selectedCountry) {
        const phoneDigits = phoneNumber.replace(/\D/g, '');
        const dialCodeDigits = selectedCountry.dialCode.replace(/\D/g, '');
        cleanPhoneNumber = dialCodeDigits + phoneDigits;
        setHasMaxUserPhone?.(true);
      }

      const wsMessage = {
        messageContentType: WebsocketMessageContentType.Text,
        message: data.message,
        messageJson: '',
        attachments: data.attachments?.map(({ name, type, ...restProps }) => ({
          ...restProps,
          fileName: name,
          contentType: type,
        })),
        entryPointGateway: entryPointGateway,
        maxUserPhone: cleanPhoneNumber,
      } as Message;

      send({
        body: wsMessage,
        destination: WebsocketEvents.AppMessage,
      });

      // Добавляем новое сообщение в ленту сообщений
      // Новое сообщение добавляется сверху, так как лента рисуется через column-reverse, то есть порядок элементов развернут
      setMessages?.((prevMessages) => [
        {
          ...newMessageTemplate,
          authorName: localeMessages.chat.you,
          createDate: new Date().toISOString().replace('Z', ''),
          attachments: data.attachments?.map((file) => ({ ...file, fileName: file.name, contentType: file.type })),
          message: data.message,
        } as Message,
        ...disableLatestCsatButtons(prevMessages),
      ]);
    },
    [
      disableLatestCsatButtons,
      handleCaptchaOpen,
      send,
      setMessages,
      entryPointGateway,
      requirePhone,
      phoneNumber,
      selectedCountry,
      setHasMaxUserPhone,
      localeMessages,
    ]
  );

  return {
    /** Состояние редактора. Показывает или заблокированное состояние или строчку редактора */
    editorLock,
    /** Показ редактора */
    unlockEditor,
    /** Отправка сообщений из редактора */
    onSubmit,
  };
};
